/**
 * Enter applies, Ctrl+Enter applies from anywhere (W2-05; DESIGN.md §5.8 "Enter applies one transaction; Esc
 * cancels"), the keyboard pattern of every generator dialog, used by the templates dialog and the Bezier editor
 * dialog (Escape is the Dialog's own):
 *  - Enter keeps its own meaning on buttons (not on checkboxes, radios and switches, which Enter does not toggle),
 *    links, lists, menus, tabs, comboboxes, multi-line text and anything expanded;
 *  - in a one-line text field the Enter that ends typing only commits the field (its own handler), so the result can
 *    be checked first; the next Enter there applies. A field that shows an error after its Enter never applies;
 *  - Ctrl+Enter (Cmd+Enter) first lets the focused field commit what was typed (blur), then applies.
 * Applying is deferred by one task so the field's commit has re-rendered the dialog first.
 */

import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type FocusEvent,
  type KeyboardEvent,
  type SyntheticEvent,
} from 'react';

const ENTER_OWNERS = [
  'button:not([role="checkbox"]):not([role="radio"]):not([role="switch"])',
  'a[href]',
  'select',
  'textarea',
  '[contenteditable="true"]',
  '[role="button"]',
  '[role="menuitem"]',
  '[role="tab"]',
  '[role="option"]',
  '[role="combobox"]',
  '[role="listbox"]',
  '[aria-expanded="true"]',
].join(', ');

const TEXT_ENTRY = 'input:not([type="checkbox"]):not([type="radio"]):not([type="button"]):not([type="submit"])';

/** Whether Enter on `target` already does something of its own (activate, open, choose, new line). */
export function enterHasOwnMeaning(target: Element): boolean {
  return target.closest(ENTER_OWNERS) !== null;
}

/** A one-line text field (its Enter commits the typed text). */
export function isTextEntry(target: Element): boolean {
  return target.matches(TEXT_ENTRY);
}

export interface ApplyKeys {
  readonly onKeyDown: (e: KeyboardEvent<HTMLElement>) => void;
  readonly onInput: (e: SyntheticEvent<HTMLElement>) => void;
  readonly onBlur: (e: FocusEvent<HTMLElement>) => void;
}

/**
 * Handlers to spread on the element around a dialog's fields (and its footer). `apply` and `disabled` are read when
 * the deferred apply runs, so they reflect the field's commit.
 */
export function useApplyKeys(apply: () => void, disabled: boolean): ApplyKeys {
  const latest = useRef({ apply, disabled });
  const alive = useRef(true);
  useEffect(() => {
    latest.current = { apply, disabled };
  });
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  /** Text fields typed in since their last commit (Enter or blur). */
  const [typed] = useState(() => new WeakSet<EventTarget>());

  const onInput = useCallback(
    (e: SyntheticEvent<HTMLElement>) => {
      if (e.target instanceof Element && isTextEntry(e.target)) typed.add(e.target);
    },
    [typed],
  );
  const onBlur = useCallback(
    (e: FocusEvent<HTMLElement>) => {
      typed.delete(e.target);
    },
    [typed],
  );
  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>) => {
      if (e.key !== 'Enter' || e.nativeEvent.isComposing || e.altKey || e.shiftKey) return;
      const target = e.target instanceof Element ? e.target : null;
      const ctrl = e.ctrlKey || e.metaKey;
      if (!ctrl && target !== null) {
        if (enterHasOwnMeaning(target)) return;
        if (isTextEntry(target) && typed.has(target)) {
          // This Enter commits the typed text (the field's own handler); the next one applies.
          typed.delete(target);
          return;
        }
      }
      e.preventDefault();
      const blurred = ctrl && target instanceof HTMLElement && isTextEntry(target) ? target : null;
      blurred?.blur();
      setTimeout(() => {
        if (!alive.current) return;
        const active = document.activeElement;
        const refused = latest.current.disabled || (!ctrl && active?.getAttribute('aria-invalid') === 'true');
        if (!refused) {
          latest.current.apply();
          return;
        }
        // Not applied: give the field its focus back (its commit has run).
        if (blurred?.isConnected === true) blurred.focus();
      }, 0);
    },
    [typed],
  );
  return { onKeyDown, onInput, onBlur };
}
